/* eslint-disable @typescript-eslint/no-unused-vars */
import { IconButton, styled, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Edit } from '@mui/icons-material';

interface EditableChipProps<T> {
  label: string;
  value?: T;
  status?: string;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  children?: JSX.Element | JSX.Element[];
  prepend?: JSX.Element;
  onClickEdit: (value?: T) => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Desativado':
      return '#FE6F50';
    case 'Ativo':
      return '#FEE250';
    default:
      return '#313131';
  }
};

const useStyles = makeStyles(({ palette, spacing }: Theme) => ({
  label: {
    color: palette.neutral[100],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    color: palette.neutral[100],
  },
  buttonIcon: {
    padding: '0 !important',
    width: '32px',
    height: '32px',
  },
  info: {
    display: 'flex',
    whiteSpace: 'nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 0 0',
    overflow: 'hidden',
    justifyContent: 'center',
    height: '100%',
  },
  prepend: {
    height: '100%',
    width: '25%',
    maxWidth: '120px',
    padding: `${spacing(1)} 0px`,
    display: 'flex',
    alignItems: 'center',
    marginRight: spacing(2),
  },
  statusLabel: {
    color: '#1c1c1c',
    marginLeft: '10px',
    padding: '4px 8px',
    borderRadius: '4px',
  }
}));

const BlocContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'minWidth' && prop !== 'maxWidth' && prop !== 'width',
})<{
  minWidth?: string;
  maxWidth?: string;
  width?: string;
}>(({ width, minWidth, maxWidth, theme }) => ({
  minWidth: minWidth || '200px',
  maxWidth: maxWidth || '340px',
  width: width || `calc(20% - (${theme.spacing(1.6)}))`,
  display: 'flex',
  background: theme.palette.neutral[400],
  borderRadius: '4px',
  padding: `${theme.spacing(2)}`,
  minHeight: '92px',
  alignItems: 'center',
  border: `0.5px solid ${theme.palette.neutral[200]}`,
}));

export const EditableBloc = <T,>({
  label,
  value,
  status,
  width,
  minWidth,
  maxWidth,
  children,
  prepend,
  onClickEdit,
}: EditableChipProps<T>) => {
  const styles = useStyles();

  return (
    <BlocContainer minWidth={minWidth} maxWidth={maxWidth} width={width}>
      {prepend && <div className={styles.prepend}>{prepend}</div>}
      <div className={styles.content}>
        <div className={styles.info}>
          <div className={styles.label}>
            {label}

            {status && (
              <div className={styles.statusLabel} style={{ backgroundColor: getStatusColor(status) }}>
                {status}
              </div>
            )}
          </div>
          <IconButton className={styles.buttonIcon} onClick={() => onClickEdit(value)}>
            <Edit className={styles.icon} />
          </IconButton>
        </div>
        {children && <div style={{ flex: '1 0 0' }}>{children}</div>}
      </div>
    </BlocContainer>
  );
};

export default EditableBloc;
